// components/GlobalDialog.tsx
"use client";

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { SquareCheckBigIcon, XIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import BounceLoader from "react-spinners/BounceLoader";
import { AnimatePresence, motion } from "motion/react";
import useTimer from "@/app/project_components/useTimer";
import { Input } from "./general/input";
import { useDialogStore } from "@/lib/store/globalDialogStore";
import { NegativeButton } from "./general/NegativeButton";
import { PositiveButton } from "./general/PositiveButton";
import { Separator } from "./general/Separator";

export default function GlobalDialog() {
  const {
    type,
    open,
    loading,
    title,
    description,
    loadingMessage,
    saveText,
    input,
    compareValue,
    cancelText,
    showSecond,
    inputType,
    errorMsg,
    setInput,
    setLoading,
    onConfirm,
    onCancel,
    closeDialog,
  } = useDialogStore();

  const inputRef = useRef<HTMLInputElement>(null);
  const { formattedElapsedTime, startTimer } = useTimer();

  const showInput = type === "CREATE" || type === "UPDATE" || type === "DELETE" || type === "INPUT";
  const isDelete = type === "DELETE";

  // 줄바꿈 제거하여 비교
  const normalizeText = (text: string) => text.replace(/[\r\n]+/g, " ").trim();
  const confirmDisabled =
    loading ||
    (type === "DELETE"
      ? normalizeText(input) !== normalizeText(compareValue || "")
      : false);

  const desc = isDelete ? (
    <>
      {description}
      <p>
        이 작업은 되돌릴 수 없습니다. 정말 삭제하려면 &ldquo;
        <strong style={{ color: "red" }}>
          {normalizeText(compareValue || "")}
        </strong>
        &rdquo; 를 입력하고 삭제 버튼을 누르세요.
      </p>
    </>
  ) : (
    description
  );

  // 버튼 기본 레이블 결정
  const confirmLabel =
    saveText ??
    (type === "CREATE"
      ? "생성"
      : type === "UPDATE"
      ? "변경"
      : type === "DELETE"
      ? "삭제"
      : "확인");

  useEffect(() => {
    if (open && showInput) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, showInput]);

  useEffect(() => {
    if (input.trim() !== "" && showInput) {
      // input이 변경되면 errorMsg 초기화 (store의 setErrorMsg 사용)
      const { setErrorMsg } = useDialogStore.getState();
      setErrorMsg("");
    }
  }, [input, showInput]);

  if (!open) return null;

  const handleConfirm = () => {
    if (input.trim() === "" && showInput) {
      const { setErrorMsg } = useDialogStore.getState();
      setErrorMsg("❗️ 내용을 입력해주세요");
      return;
    }

    // INPUT 타입이 아닌 경우에만 loading 상태로 전환
    if (type !== "INPUT") {
      setLoading(true);
      if (showSecond) {
        startTimer();
      }
    }

    onConfirm?.(showInput ? input.trim() : undefined);
  };

  const handleCancel = () => {
    onCancel?.();
    closeDialog();
  };

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          static
          open={open}
          onClose={handleCancel}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/60" />

          {/* 다이얼로그 컨테이너 */}
          <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="relative w-full max-w-lg"
            >
              <DialogPanel className="overflow-hidden rounded-lg bg-white dark:bg-zinc-800 p-6 text-left shadow-xl">
                {/* 닫기 버튼 */}
                {!loading && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={loading}
                    className="absolute right-4 top-4 rounded-md text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" />
                  </button>
                )}

                {/* 제목 & 설명 (기존 내용과 동일) */}
                <div className="mb-6">
                  <DialogTitle className="flex items-center text-lg font-bold text-gray-900 dark:text-white">
                    <SquareCheckBigIcon className="h-6 w-6 mr-2" />
                    {title}
                  </DialogTitle>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    {desc}
                  </div>

                  {/* 입력창 */}
                  {showInput && (
                    <>
                      <Input
                        className="mt-6 w-full"
                        ref={inputRef}
                        type={inputType || "text"}
                        value={input}
                        disabled={loading}
                        onChange={(e) => setInput(e.target.value)}
                      />
                      <p className="text-red-600 text-xs mt-1 h-2">
                        {errorMsg}
                      </p>
                    </>
                  )}
                </div>

                <Separator />

                {/* 로딩 중 UI 또는 버튼 (기존 내용과 동일) */}
                {loading ? (
                  <div className="pt-4 flex items-center gap-2 text-sm">
                    {showSecond ? (
                      <div className="flex w-full items-center justify-between">
                        <span className="font-bold">
                          {formattedElapsedTime}s
                        </span>
                        <div className="flex items-center gap-2">
                          <BounceLoader size={15} color="blue" />
                          {loadingMessage}
                        </div>
                      </div>
                    ) : (
                      <div className="flex w-full items-center justify-end gap-2">
                        <BounceLoader size={15} color="blue" />
                        {loadingMessage}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="pt-4 flex justify-end gap-4">
                    <NegativeButton onClick={handleCancel}>
                      {cancelText}
                    </NegativeButton>
                    <PositiveButton
                      onClick={handleConfirm}
                      disabled={confirmDisabled}
                    >
                      {confirmLabel}
                    </PositiveButton>
                  </div>
                )}
              </DialogPanel>
            </motion.div>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
